import { authService } from '../services/authService';

/**
 * Get the current user's role type for a specific department
 * @param department - The department to check (defaults to active department)
 * @returns 'approver' | 'requester' | 'both' | null
 */
export const getUserRoleType = (department?: string): 'approver' | 'requester' | 'both' | null => {
  const user = authService.getUser();
  if (!user) return null;

  const activeDepartment = department || authService.getActiveDepartment();
  
  // Check if user has roles array
  if (user.roles && Array.isArray(user.roles) && user.roles.length > 0) {
    // Find the role for the specified department
    const departmentRole = user.roles.find((r: any) => r.department === activeDepartment);
    
    if (departmentRole && departmentRole.roleId) {
      // roleId can be an object (populated) or string (ID)
      const role = typeof departmentRole.roleId === 'object' 
        ? departmentRole.roleId 
        : null;
      
      if (role && role.roleType) {
        return role.roleType;
      }
    }
  }
  
  // Fallback: check the main role object if roles array doesn't have the department
  if (user.role && typeof user.role === 'object' && user.role.roleType) {
    // Only return if department matches
    if (user.role.department === activeDepartment) {
      return user.role.roleType;
    }
  }
  
  return null;
};

/**
 * Check if the current user is an approver for a specific department
 * @param department - The department to check (defaults to active department)
 * @returns boolean
 */
export const isApprover = (department?: string): boolean => {
  const roleType = getUserRoleType(department);
  return roleType === 'approver' || roleType === 'both';
};

/**
 * Check if the current user is a requester for a specific department
 * @param department - The department to check (defaults to active department)
 * @returns boolean
 */
export const isRequester = (department?: string): boolean => {
  const roleType = getUserRoleType(department);
  return roleType === 'requester' || roleType === 'both';
};

/**
 * Check if approval buttons (resubmit, send for review) should be shown
 * @param department - The department to check (defaults to active department)
 * @returns boolean - true if buttons should be shown (requester), false if hidden (approver)
 */
export const shouldShowApprovalButtons = (department?: string): boolean => {
  const activeDepartment = department || authService.getActiveDepartment();
  const roleType = getUserRoleType(activeDepartment);
  
  // If roleType is 'approver' and department is 'AMS', hide buttons
  if (roleType === 'approver' && activeDepartment === 'AMS') {
    return false;
  }
  
  // If roleType is 'requester' or 'both', show buttons
  if (roleType === 'requester' || roleType === 'both') {
    return true;
  }
  
  // Default: show buttons (for backward compatibility)
  return true;
};
